/* ================================================================
   AISTROWISE — Rueda de carta natal real
   Dibuja la rueda con las posiciones REALES de la respuesta de
   POST /natal-positions, en el cliente, sin librerías y sin red.
   Si falta cualquier dato (ascendente, casas) o algo falla al
   dibujar, la rueda decorativa (estática) se mantiene tal cual.
   Mismo patrón dual Node/navegador que assets/aw-teaser.js.
   PRIVACY: la respuesta llega por llamada directa guardada
   (window.AWWheel.draw), nunca por evento de documento.
   ================================================================ */
(function (global) {
  'use strict';

  var SIGNS = ['aries', 'tauro', 'geminis', 'cancer', 'leo', 'virgo', 'libra', 'escorpio', 'sagitario', 'capricornio', 'acuario', 'piscis'];
  var SIGN_GLYPHS = {
    aries: '♈', tauro: '♉', geminis: '♊', cancer: '♋',
    leo: '♌', virgo: '♍', libra: '♎', escorpio: '♏',
    sagitario: '♐', capricornio: '♑', acuario: '♒', piscis: '♓'
  };
  var HARD_ASPECTS = ['cuadratura', 'oposicion'];
  var DRAWN_ASPECTS = ['cuadratura', 'oposicion', 'trigono', 'sextil']; // conjunción skipped: adjacent points need no line

  function norm(s) {
    return String(s || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim();
  }

  /* ── pure geometry (Node-testable) ──────────────────────────── */

  function lonOf(body) {
    if (!body) return null;
    var idx = SIGNS.indexOf(norm(body.sign_es));
    if (idx === -1) return null;
    if (!body.degree || body.degree.deg == null || body.degree.min == null) return null;
    return idx * 30 + body.degree.deg + body.degree.min / 60;
  }

  function angleFor(lon, ascLon) {
    var phi = (180 + (lon - ascLon)) % 360;
    if (phi < 0) phi += 360;
    return phi;
  }

  function pointAt(phiDeg, r, cx, cy) {
    var rad = phiDeg * Math.PI / 180;
    return { x: cx + r * Math.cos(rad), y: cy - r * Math.sin(rad) };
  }

  /* Collision spread for planet glyphs. Clusters (circular, consecutive
     members within minGap) are fanned evenly around the cluster's angular
     midpoint with exactly minGap spacing, preserving relative order.
     Lone points are returned unchanged (mod 360). */
  function fanOut(lons, minGap) {
    var n = lons.length;
    var norm360 = lons.map(function (lon) { return ((lon % 360) + 360) % 360; });
    if (n <= 1) return norm360.slice();

    var order = norm360.map(function (lon, i) { return i; })
      .sort(function (a, b) { return norm360[a] - norm360[b]; });

    // circular gaps between consecutive sorted entries
    var gaps = [];
    for (var k = 0; k < n; k++) {
      var curLon = norm360[order[k]];
      var nextLon = norm360[order[(k + 1) % n]];
      gaps.push(k === n - 1 ? (nextLon + 360 - curLon) : (nextLon - curLon));
    }
    // seam = the largest gap: a safe place to cut the circle into a line
    // (guarantees no real cluster gets split across the wrap point)
    var seam = 0;
    for (var g = 1; g < n; g++) { if (gaps[g] > gaps[seam]) seam = g; }

    // walk the circle starting right after the seam, unwrapping past 360
    var seq = [];
    for (var s = 0; s < n; s++) {
      var pos = (seam + 1 + s) % n;
      var origIdx = order[pos];
      var lon = norm360[origIdx];
      if (seq.length === 0) { seq.push({ i: origIdx, lon: lon }); }
      else {
        var prev = seq[seq.length - 1].lon;
        while (lon < prev) lon += 360;
        seq.push({ i: origIdx, lon: lon });
      }
    }

    // cluster: break whenever the gap to the previous member is >= minGap
    var clusters = [[seq[0]]];
    for (var c = 1; c < seq.length; c++) {
      if (seq[c].lon - seq[c - 1].lon < minGap) clusters[clusters.length - 1].push(seq[c]);
      else clusters.push([seq[c]]);
    }

    var result = new Array(n);
    clusters.forEach(function (cluster) {
      var m = cluster.length;
      if (m === 1) { result[cluster[0].i] = ((cluster[0].lon % 360) + 360) % 360; return; }
      var mid = 0;
      cluster.forEach(function (c2) { mid += c2.lon; });
      mid = mid / m;
      cluster.forEach(function (c2, idx2) {
        var displayLon = mid + (idx2 - (m - 1) / 2) * minGap;
        result[c2.i] = ((displayLon % 360) + 360) % 360;
      });
    });
    return result;
  }

  /* Full layout as plain data (no DOM). Returns null if the Ascendant
     can't be located (no angles entry, no house-1 fallback). */
  function layoutWheel(data, opts) {
    opts = opts || {};
    var o = {
      cx: opts.cx != null ? opts.cx : 115,
      cy: opts.cy != null ? opts.cy : 115,
      signR: opts.signR != null ? opts.signR : 103,
      signInner: opts.signInner != null ? opts.signInner : 96,
      signOuter: opts.signOuter != null ? opts.signOuter : 110,
      planetR: opts.planetR != null ? opts.planetR : 78,
      aspectR: opts.aspectR != null ? opts.aspectR : 58,
      cuspInner: opts.cuspInner != null ? opts.cuspInner : 58,
      cuspOuter: opts.cuspOuter != null ? opts.cuspOuter : 96,
      tickInner: opts.tickInner != null ? opts.tickInner : 89,
      minGap: opts.minGap != null ? opts.minGap : 9
    };

    var angles = (data && data.angles) || [];
    var houses = (data && data.houses) || [];
    var planetsIn = (data && data.planets) || [];
    var aspectsIn = (data && data.aspects) || [];

    var ascEntry = null;
    for (var i = 0; i < angles.length; i++) {
      if (norm(angles[i].name_es) === 'ascendente') { ascEntry = angles[i]; break; }
    }
    var casaUno = null;
    for (var hf = 0; hf < houses.length; hf++) { if (houses[hf] && houses[hf].house === 1) { casaUno = houses[hf]; break; } }
    var ascLon = ascEntry ? lonOf(ascEntry) : (casaUno ? lonOf(casaUno) : null);
    if (ascLon == null) return null;

    // signs + dividers
    var signs = [], dividers = [];
    for (var sIdx = 0; sIdx < 12; sIdx++) {
      var midPt = pointAt(angleFor(sIdx * 30 + 15, ascLon), o.signR, o.cx, o.cy);
      signs.push({ glyph: SIGN_GLYPHS[SIGNS[sIdx]], x: midPt.x, y: midPt.y });
      var boundPhi = angleFor(sIdx * 30, ascLon);
      var dp1 = pointAt(boundPhi, o.signInner, o.cx, o.cy);
      var dp2 = pointAt(boundPhi, o.signOuter, o.cx, o.cy);
      dividers.push({ x1: dp1.x, y1: dp1.y, x2: dp2.x, y2: dp2.y });
    }

    // cusps
    var cusps = [];
    houses.forEach(function (h) {
      var lon = lonOf(h);
      if (lon == null) return;
      var phi = angleFor(lon, ascLon);
      var p1 = pointAt(phi, o.cuspInner, o.cx, o.cy);
      var p2 = pointAt(phi, o.cuspOuter, o.cx, o.cy);
      cusps.push({ house: h.house, x1: p1.x, y1: p1.y, x2: p2.x, y2: p2.y, major: (h.house === 1 || h.house === 10) });
    });

    // planets (excluding nodo sur — always opposite the north node, clutters)
    var drawn = [];
    planetsIn.forEach(function (p) {
      if (norm(p.name_es).indexOf('nodo sur') === 0) return; // API names it 'Nodo Sur Verdadero'
      var lon = lonOf(p);
      if (lon == null) return;
      drawn.push({ glyph: p.glyph, name_es: p.name_es, trueLon: lon });
    });
    drawn.sort(function (a, b) { return a.trueLon - b.trueLon; });
    var displayLons = fanOut(drawn.map(function (d) { return d.trueLon; }), o.minGap);
    var planets = drawn.map(function (d, idx) {
      var phi = angleFor(displayLons[idx], ascLon);
      var pt = pointAt(phi, o.planetR, o.cx, o.cy);
      // tick at the TRUE longitude on the sign-band edge: anchors the glyph
      // (which may be fanned aside) to its real zodiac position
      var truePhi = angleFor(d.trueLon, ascLon);
      var t1 = pointAt(truePhi, o.tickInner, o.cx, o.cy);
      var t2 = pointAt(truePhi, o.cuspOuter, o.cx, o.cy);
      return { glyph: d.glyph, name_es: d.name_es, x: pt.x, y: pt.y, trueLon: d.trueLon,
        tick: { x1: t1.x, y1: t1.y, x2: t2.x, y2: t2.y } };
    });

    // aspects — endpoints at TRUE longitude (not fanned), major types only
    var lonByName = {};
    drawn.forEach(function (d) { lonByName[norm(d.name_es)] = d.trueLon; });
    var aspects = [];
    aspectsIn.forEach(function (a) {
      var type = norm(a.type_es);
      if (DRAWN_ASPECTS.indexOf(type) === -1) return;
      var n1 = norm(a.p1_es), n2 = norm(a.p2_es);
      if (lonByName[n1] === undefined || lonByName[n2] === undefined) return; // e.g. nodo sur, unknown
      var pt1 = pointAt(angleFor(lonByName[n1], ascLon), o.aspectR, o.cx, o.cy);
      var pt2 = pointAt(angleFor(lonByName[n2], ascLon), o.aspectR, o.cx, o.cy);
      aspects.push({ x1: pt1.x, y1: pt1.y, x2: pt2.x, y2: pt2.y, hard: HARD_ASPECTS.indexOf(type) > -1 });
    });

    // structural rings the live wheel draws itself (the decorative dotted
    // circles are hidden once real data renders)
    var rings = [
      { r: o.signOuter, cls: 'outer' },
      { r: o.cuspOuter, cls: 'band' },
      { r: o.aspectR, cls: 'hub' }
    ];
    var band = { r: (o.signOuter + o.cuspOuter) / 2, width: o.signOuter - o.cuspOuter };

    return { cx: o.cx, cy: o.cy, size: o.cx * 2, rings: rings, band: band,
      signs: signs, dividers: dividers, cusps: cusps, planets: planets, aspects: aspects };
  }

  var api = {
    SIGNS: SIGNS, SIGN_GLYPHS: SIGN_GLYPHS,
    lonOf: lonOf, angleFor: angleFor, pointAt: pointAt, fanOut: fanOut, layoutWheel: layoutWheel,
    _norm: norm
  };
  if (typeof module !== 'undefined' && module.exports) { module.exports = api; }
  if (typeof global.window !== 'undefined') { global.AWWheel = api; }

  /* ── browser wiring (inert under Node) ──────────────────────── */
  if (typeof document !== 'undefined') {
    // Same monochrome-glyph fix as aw-calculadora.js, replicated verbatim
    // (zodiac glyphs ♈-♓ fall inside the ☀-➿ range already covered here).
    function forceText(s) {
      return String(s == null ? '' : s).replace(/([←-⇿☀-➿])/g, '$1︎');
    }

    function svgEl(name, attrs) {
      var el = document.createElementNS('http://www.w3.org/2000/svg', name);
      for (var k in attrs) { if (Object.prototype.hasOwnProperty.call(attrs, k)) el.setAttribute(k, attrs[k]); }
      return el;
    }

    function removeLive(svg) {
      var live = svg.querySelector('[data-aw-wheel-live]');
      if (live && live.parentNode) live.parentNode.removeChild(live);
    }

    api.draw = function (data, root) {
      var svg = (root || document).querySelector('[data-aw-calc-wheel]');
      if (!svg) return;
      var layout = layoutWheel(data);
      if (!layout) return; // no ascendant/houses to anchor on: decor stays

      var decor = svg.querySelector('[data-aw-wheel-decor]');
      removeLive(svg); // recalculations must not stack

      // sign glyphs live as HTML spans over the svg when the wrapper exists:
      // iOS Safari ignores the U+FE0E text-presentation selector inside SVG
      // <text> and paints the zodiac glyphs as color emoji. HTML text honors
      // it (the positions list below the wheel proves it on this same page).
      var wrap = (root || document).querySelector('[data-aw-calc-wheel-wrap]');
      if (wrap) {
        Array.prototype.slice.call(wrap.querySelectorAll('.aw-wheel__sign')).forEach(function (el) {
          if (el.parentNode) el.parentNode.removeChild(el);
        });
      }

      try {
        var g = svgEl('g', {});
        g.setAttribute('data-aw-wheel-live', '');

        // 0. structure: sign band tint + rings (the live wheel's own skeleton)
        g.appendChild(svgEl('circle', {
          cx: layout.cx, cy: layout.cy, r: layout.band.r, fill: 'none',
          stroke: '#6B6AF2', 'stroke-width': layout.band.width, 'stroke-opacity': '0.08'
        }));
        layout.rings.forEach(function (ring) {
          g.appendChild(svgEl('circle', {
            cx: layout.cx, cy: layout.cy, r: ring.r, fill: 'none',
            stroke: '#6B6AF2', 'stroke-width': ring.cls === 'hub' ? '0.8' : '1',
            'stroke-opacity': ring.cls === 'hub' ? '0.35' : '0.5'
          }));
        });

        // 1. aspect lines (endpoints on the hub ring)
        layout.aspects.forEach(function (a) {
          var line = svgEl('line', {
            x1: a.x1, y1: a.y1, x2: a.x2, y2: a.y2,
            stroke: '#8F8EF7',
            'stroke-width': a.hard ? '1.1' : '0.9',
            'stroke-opacity': a.hard ? '0.8' : '0.5'
          });
          if (!a.hard) line.setAttribute('stroke-dasharray', '2.5 2.5');
          g.appendChild(line);
        });

        // 2. cusp lines (hub ring to sign band; ASC/MC axes emphasized)
        layout.cusps.forEach(function (c) {
          g.appendChild(svgEl('line', {
            x1: c.x1, y1: c.y1, x2: c.x2, y2: c.y2,
            stroke: '#6B6AF2',
            'stroke-width': c.major ? '1.3' : '0.8',
            'stroke-opacity': c.major ? '0.85' : '0.45'
          }));
        });

        // 3. sign divider ticks across the band
        layout.dividers.forEach(function (d) {
          g.appendChild(svgEl('line', {
            x1: d.x1, y1: d.y1, x2: d.x2, y2: d.y2,
            stroke: '#6B6AF2', 'stroke-width': '0.8', 'stroke-opacity': '0.5'
          }));
        });

        // 4. planet position ticks at the TRUE longitude (band edge)
        layout.planets.forEach(function (p) {
          g.appendChild(svgEl('line', {
            x1: p.tick.x1, y1: p.tick.y1, x2: p.tick.x2, y2: p.tick.y2,
            stroke: '#D7D3FF', 'stroke-width': '0.9', 'stroke-opacity': '0.65'
          }));
        });

        // 5. sign glyphs — HTML overlay preferred (see note above); SVG fallback
        if (wrap) {
          layout.signs.forEach(function (sg) {
            var span = document.createElement('span');
            span.className = 'aw-wheel__sign';
            span.setAttribute('aria-hidden', 'true');
            span.style.left = (sg.x / layout.size * 100) + '%';
            span.style.top = (sg.y / layout.size * 100) + '%';
            span.textContent = forceText(sg.glyph);
            wrap.appendChild(span);
          });
        } else {
          layout.signs.forEach(function (sg) {
            var t = svgEl('text', {
              x: sg.x, y: sg.y, 'font-size': '9', fill: '#8B87C9',
              'text-anchor': 'middle', 'dominant-baseline': 'central'
            });
            t.textContent = forceText(sg.glyph);
            g.appendChild(t);
          });
        }

        // 6. planet glyphs (fanned positions). Dark halo via paint-order so
        // glyphs stay readable where cusp/aspect lines pass underneath.
        layout.planets.forEach(function (p) {
          var t = svgEl('text', {
            x: p.x, y: p.y, 'font-size': '12', fill: '#EDEBFF',
            stroke: '#141126', 'stroke-width': '3', 'paint-order': 'stroke',
            'stroke-linejoin': 'round',
            'text-anchor': 'middle', 'dominant-baseline': 'central'
          });
          t.textContent = forceText(p.glyph);
          g.appendChild(t);
        });

        svg.appendChild(g);
        if (decor) decor.setAttribute('display', 'none');
      } catch (e) {
        removeLive(svg);
        if (decor) decor.removeAttribute('display');
        if (wrap) {
          Array.prototype.slice.call(wrap.querySelectorAll('.aw-wheel__sign')).forEach(function (el) {
            if (el.parentNode) el.parentNode.removeChild(el);
          });
        }
      }
    };
  }
})(typeof window !== 'undefined' ? window : globalThis);
